import type { Amount, SignInfo } from '@shared/core-types';
import type {
    AggregatedConvertToStandardIfInstantFails,
    AggregatedPayeeVerificationResults,
    Datacarrier,
    PayeeVerificationResult,
    RecipientAddress,
} from '@api-client/payments-webapi';

import type { Accountno, ORDER_CATEGORY, ORDER_TYPE, PaymentOrderAdditionalInfo } from './Payments';
import type {
    AUTHORIZATION_STATUS,
    CHANNEL_ID,
    ORDER_FORM,
    ORDER_SOURCE,
} from './payment-order/PaymentOrderConstants';
import type { ProductSource, StateInfo } from './payment-order/PaymentOrderTypes';
import type { STATE, STATE_DETAIL } from './payment-order/PaymentState';

export enum DATA_CARRIER_TYPE {
    PACKAGE = 'PACKAGE',
    ORDERS = 'ORDERS',
    TRANSACTIONS = 'TRANSACTIONS',
}

export enum DATA_CARRIER_SUB_TYPE {
    SALARY = 'SALARY',
    STANDARD = 'STANDARD',
    INTERNATIONAL = 'INTERNATIONAL',
}

export enum DATA_CARRIER_STATE {
    OPEN = 'OPEN',
    ERROR = 'ERROR',
    CLOSED = 'CLOSED',
    PENDING = 'PENDING',
    MIXED = 'MIXED',
}

export enum DATA_CARRIER_FLAG {
    COLLECTIVE = 'collectiveSigning',
    DELETABLE = 'deletable',
    PRINTABLE = 'printable',
    ORDER_NOT_INCLUDED = 'ordersNotIncluded',
    SIGNABLE = 'signable', // not group spec aligned, just AT speciality
    PARTIAL_CONTENT_PERMISSION = 'partialContentPermission',
    NO_CONTENT_PERMISSION = 'noContentPermission',
    TRANSFER_DATE_WHEN_SIGNING_DONE = 'transferDateWhenSigningDone',
    AUTO_RECREATED = 'autoRecreated',
    MULTIPLE_SENDER_ACCOUNTS = 'multipleSenderAccounts',
    PAYEE_VERIFICATION_REQUIRED = 'payeeVerificationRequired',
    BULKS_NOT_INCLUDED = 'bulksNotIncluded',
    EDITABLE = 'editable',
}

export interface BulkPaymentOrder {
    id: string;
    amount: Amount;
    receiver: Accountno;
    receiverName?: string;
    transferDate?: string;
    transferDateTime?: string;
    orderCategory: ORDER_CATEGORY;
    stateDetail?: STATE_DETAIL;
    state?: STATE;
    stateOk?: boolean;
    'cz-totalAmountWithFees'?: Amount;
    additionalInfo?: PaymentOrderAdditionalInfo;
    recipientAddress?: RecipientAddress;
    payeeVerificationResult?: PayeeVerificationResult;
}

export interface DataCarrierBulk {
    id: string;
    orderCount: number;
    sender: Accountno;
    orderType: ORDER_TYPE;
    referenceCode?: string;
    requiredExecutionDate?: string;
    bulkAmount?: Amount;
    senderName?: string;
    orders?: BulkPaymentOrder[];
    payeeVerification?: boolean;
    aggregatedPayeeVerificationResults?: AggregatedPayeeVerificationResults;
    readonly stateDetail?: STATE_DETAIL;
}

interface BaseDataCarrier {
    id: string;
    type: DATA_CARRIER_TYPE;
    flags?: DATA_CARRIER_FLAG[];
    uploadedDate: string;
    expirationDate?: string;
    uploadedBy?: string;
    name?: string;
    sumAmount?: Amount;
    'at-controlSum'?: number;
    controlSum?: number;
    signInfo?: SignInfo;
    authorizations?: Authorization[];
    identification?: string;
    bulks?: DataCarrierBulk[];
    creationDate?: string;
    aggregatedPayeeVerificationResults?: AggregatedPayeeVerificationResults;

    /**
     * Datetime when bundle or its state was last modified (read-only field provided by BE system).
     */
    modificationDate?: string;
    /**
     * Array of Outgoing sums of order amounts (debiting sender accounts) grouped by currencies.
     * For Collective Signing this structure is mandatory if `sumIncomingsByCurrency` is not provided
     */
    readonly sumOutgoingsByCurrency?: Amount[];
    /**
     * Array of Incoming sums of order amounts (crediting sender accounts) grouped by currencies.
     * For Collective Signing this structure is mandatory if `sumOutgoingsByCurrency` is not provided
     */
    readonly sumIncomingsByCurrency?: Amount[];

    readonly stateDetail?: STATE_DETAIL;

    /**
     * Earliest transfer date of nested orders
     */
    readonly transferDate?: string;
    readonly transferDateTime?: string;

    readonly orderForm: ORDER_FORM;
    readonly channelId?: string;
    readonly childItemsCount?: number;
}

export interface RetailDataCarrier extends BaseDataCarrier {
    orderCount?: number;
    state?: DATA_CARRIER_STATE;
}

export interface BusinessDataCarrier extends BaseDataCarrier {
    readonly itemsCount?: number;
    readonly state: STATE;
    readonly cosigningExpirationDate?: string;
    readonly productSource?: ProductSource;
    readonly receiverBankName?: string;
    readonly receiverBankAddress?: string[];
    readonly stateInfo?: StateInfo;
    readonly orderSource?: ORDER_SOURCE;
    readonly channelId?: CHANNEL_ID;
    readonly additionalDisplayInfo?: Datacarrier['additionalDisplayInfo'];
    readonly creditTransfersCount?: number;
    readonly directDebitsCount?: number;
    readonly aggregatedPayeeVerificationResults?: AggregatedPayeeVerificationResults;
    readonly aggregatedConvertToStandardIfInstantFails?: AggregatedConvertToStandardIfInstantFails;
}

export type DataCarrier = RetailDataCarrier | BusinessDataCarrier;

interface Authorization {
    timeStamp: string;
    clientName: string;
    userId?: string;
    status: AUTHORIZATION_STATUS;
}
